import React, { useState, useEffect, useMemo, useCallback } from "react";
import { isValidEin, isEinVerifiedFromFmcsa, maskEinInput } from "@/lib/ein";
import { ShieldCheck, Eye as EyeIcon, ChevronDown, ChevronRight } from "lucide-react";
import SSNInput from "@/components/SSNInput";
import PhoneInput from "@/components/PhoneInput";

const FIELDS = [
    { group: "Company", items: [
        { key: "company_name", label: "Company Name", required: true },
        { key: "dba", label: "Doing Business As (DBA)" },
        { key: "company_address", label: "Company Address" },
        { key: "ein_tax_id", label: "EIN / Tax ID", placeholder: "XX-XXXXXXX", einMask: true },
        { key: "usdot_number", label: "USDOT Number", placeholder: "e.g. 1234567", inputMode: "numeric", maxDigits: 8, helper: "5–8 digit USDOT number issued by FMCSA" },
        { key: "company_phone", label: "Company Phone", type: "tel" },
        { key: "billing_contact_name", label: "Billing Contact Name" },
        { key: "billing_contact_email", label: "Billing Contact Email", type: "email" },
        { key: "billing_contact_phone", label: "Billing Contact Phone", type: "tel" },
    ]},
    { group: "Guarantor", items: [
        { key: "guarantor_name", label: "Guarantor Name" },
        { key: "guarantor_ssn", label: "Guarantor SSN" },
        { key: "guarantor_home_address", label: "Guarantor Home Address" },
        { key: "guarantor_dob", label: "Guarantor Date of Birth", type: "date" },
        { key: "guarantor_phone", label: "Guarantor Phone", type: "tel" },
        { key: "guarantor_email", label: "Guarantor Email", type: "email" },
    ]},
    { group: "Insurance Agent", items: [
        { key: "agent_name", label: "Agent Name", helper: "Used to address the COI request email" },
        { key: "agent_company", label: "Agency Name" },
        { key: "agent_email", label: "Agent Email", type: "email", helper: "COI request emails are sent here when a contract is fully signed" },
        { key: "agent_phone", label: "Agent Phone", type: "tel" },
    ]},
];

const COMPOSITES = [
    { key: "trade_reference_1", label: "Trade Reference 1", subfields: [
        { key: "name", label: "Company" }, { key: "contact", label: "Contact" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" }] },
    { key: "trade_reference_2", label: "Trade Reference 2", subfields: [
        { key: "name", label: "Company" }, { key: "contact", label: "Contact" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" }] },
    { key: "trade_reference_3", label: "Trade Reference 3", subfields: [
        { key: "name", label: "Company" }, { key: "contact", label: "Contact" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" }] },
    { key: "bank_reference", label: "Bank Reference", subfields: [
        { key: "bank_name", label: "Bank Name" }, { key: "contact", label: "Contact" }, { key: "phone", label: "Phone" }, { key: "account_last4", label: "Account (last 4)" }] },
];

// Phase PROFILE-COLLAPSE — per-user collapse state persisted to
// localStorage. Keys mirror the section labels but are slugged so
// they're stable if the visible label ever changes copy. When a
// section contains a validation error (see ``fieldErrors``), the
// collapsed state is *ignored on render* so the admin can see and
// fix the error — the persisted value is left untouched so the
// preference re-applies once the error clears.
const COLLAPSE_STORAGE_KEY = "admin.customerProfile.collapsedSections";
const SECTION_KEYS = {
    Company: "company",
    Guarantor: "guarantor",
    "Insurance Agent": "insurance_agent",
    References: "references",
};
// Map each field key to its owning section slug so we can force-
// expand a section that has any live validation error. Extend when
// new sections are added to ``FIELDS``.
const FIELD_KEY_TO_SECTION = (() => {
    const map = {};
    FIELDS.forEach((s) => {
        const slug = SECTION_KEYS[s.group];
        s.items.forEach((f) => { map[f.key] = slug; });
    });
    return map;
})();

function _readCollapsed() {
    try {
        const raw = window.localStorage.getItem(COLLAPSE_STORAGE_KEY);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        return new Set(Array.isArray(arr) ? arr : []);
    } catch (_) {
        return new Set();
    }
}

function _writeCollapsed(set) {
    try {
        window.localStorage.setItem(
            COLLAPSE_STORAGE_KEY,
            JSON.stringify([...set]),
        );
    } catch (_) { /* private mode / quota — silent no-op */ }
}

function CollapsibleSection({ title, sectionKey, collapsed, forceOpen, onToggle, testidPrefix, children }) {
    const isOpen = forceOpen || !collapsed;
    const contentId = `${testidPrefix}-section-${sectionKey}-body`;
    return (
        <section>
            <button
                type="button"
                onClick={() => onToggle(sectionKey)}
                aria-expanded={isOpen}
                aria-controls={contentId}
                data-testid={`${testidPrefix}-section-${sectionKey}-toggle`}
                data-force-open={forceOpen ? "1" : undefined}
                className="mb-3 flex w-full items-center gap-2 text-left text-xs font-medium tracking-[0.18em] uppercase text-slate-500 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-900/30 rounded-sm"
            >
                {isOpen
                    ? <ChevronDown size={14} className="text-slate-400" aria-hidden="true" />
                    : <ChevronRight size={14} className="text-slate-400" aria-hidden="true" />}
                <span>{title}</span>
                {forceOpen && (
                    <span
                        className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700 normal-case tracking-normal"
                        data-testid={`${testidPrefix}-section-${sectionKey}-error-lock`}
                        title="Section contains a validation error — expanded until fixed"
                    >
                        needs attention
                    </span>
                )}
            </button>
            <div
                id={contentId}
                className={
                    "overflow-hidden transition-all duration-200 ease-out " +
                    (isOpen ? "max-h-[4000px] opacity-100" : "max-h-0 opacity-0")
                }
                aria-hidden={!isOpen}
            >
                {children}
            </div>
        </section>
    );
}

export function CustomerFieldsForm({ data, onChange, disabled, prefix = "customer-form", fmcsaQcm = null, isSuperAdmin = false, onRevealSsn = null }) {
    const setField = (k, v) => onChange({ ...data, [k]: v });
    const setSub = (k, sk, v) => onChange({ ...data, [k]: { ...(data?.[k] || {}), [sk]: v } });
    // Phase QCM-EIN — track which fields have been blurred with an
    // invalid value so we surface the inline error only after the
    // user is done typing. Keyed by field key, value is the error
    // string or empty.
    const [fieldErrors, setFieldErrors] = useState({});
    // Phase PROFILE-COLLAPSE — collapsed set persisted to localStorage.
    // ``forceOpenSlugs`` is derived from ``fieldErrors`` and overrides
    // the persisted collapse for render only (never mutates storage).
    const [collapsed, setCollapsed] = useState(_readCollapsed);
    const forceOpenSlugs = useMemo(() => {
        const s = new Set();
        Object.entries(fieldErrors).forEach(([k, v]) => {
            if (!v) return;
            const slug = FIELD_KEY_TO_SECTION[k];
            if (slug) s.add(slug);
        });
        return s;
    }, [fieldErrors]);
    const toggleSection = useCallback((slug) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(slug)) next.delete(slug); else next.add(slug);
            _writeCollapsed(next);
            return next;
        });
    }, []);
    const allSectionSlugs = Object.values(SECTION_KEYS);
    const allCollapsed = allSectionSlugs.every((k) => collapsed.has(k));
    const toggleAll = useCallback(() => {
        setCollapsed((prev) => {
            const next = allSectionSlugs.every((k) => prev.has(k))
                ? new Set()             // all currently collapsed → expand all
                : new Set(allSectionSlugs);  // otherwise collapse all
            _writeCollapsed(next);
            return next;
        });
    }, []);
    // Keep in sync with other tabs / windows editing the same key.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === COLLAPSE_STORAGE_KEY) setCollapsed(_readCollapsed());
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);
    return (
        <div className="space-y-8">
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={toggleAll}
                    className="text-xs font-medium text-slate-500 hover:text-slate-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-900/30 rounded-sm px-1"
                    data-testid={`${prefix}-collapse-toggle-all`}
                >
                    {allCollapsed ? "Expand all" : "Collapse all"}
                </button>
            </div>
            {FIELDS.map((section) => (
                <CollapsibleSection
                    key={section.group}
                    title={section.group}
                    sectionKey={SECTION_KEYS[section.group]}
                    collapsed={collapsed.has(SECTION_KEYS[section.group])}
                    forceOpen={forceOpenSlugs.has(SECTION_KEYS[section.group])}
                    onToggle={toggleSection}
                    testidPrefix={prefix}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {section.items.map((f) => {
                            // Task 11b — phone fields go through PhoneInput
                            // for live ``(XXX) XXX-XXXX`` formatting + NANP
                            // validation. Storage is bare 10 digits.
                            if (f.type === "tel") {
                                return (
                                    <div key={f.key} className="space-y-1.5">
                                        <label className="text-sm font-medium text-slate-900"
                                               htmlFor={`${prefix}-${f.key}`}>
                                            {f.label}
                                        </label>
                                        <PhoneInput
                                            data-testid={`${prefix}-${f.key}`}
                                            value={data?.[f.key] || ""}
                                            onChange={(v) => setField(f.key, v)}
                                            disabled={disabled}
                                        />
                                        {f.helper && (
                                            <p className="text-xs text-slate-500"
                                               data-testid={`${prefix}-${f.key}-help`}>{f.helper}</p>
                                        )}
                                    </div>
                                );
                            }
                            // Task 11 — guarantor SSN gets the dedicated
                            // SSNInput (live format + blur mask) plus
                            // an optional eye icon for super_admin
                            // reveal. The parent passes ``onRevealSsn``
                            // + ``isSuperAdmin`` + ``maskedLast4`` props
                            // when it wants the reveal affordance.
                            if (f.key === "guarantor_ssn") {
                                return (
                                    <div key={f.key} className="space-y-1.5">
                                        <label className="text-sm font-medium text-slate-900 flex items-center gap-2"
                                               htmlFor={`${prefix}-${f.key}`}>
                                            <span>{f.label}</span>
                                            {isSuperAdmin && (data?.guarantor_ssn_last4 || data?.guarantor_ssn_encrypted) && (
                                                <button
                                                    type="button"
                                                    onClick={onRevealSsn}
                                                    disabled={disabled}
                                                    className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                                                    data-testid={`${prefix}-${f.key}-reveal-trigger`}
                                                    title="Reveal SSN (super_admin)"
                                                >
                                                    <EyeIcon size={11} /> Reveal
                                                </button>
                                            )}
                                        </label>
                                        <SSNInput
                                            data-testid={`${prefix}-${f.key}`}
                                            value={data?.[f.key] || ""}
                                            onChange={(v) => setField(f.key, v)}
                                            disabled={disabled}
                                            maskedLast4={data?.guarantor_ssn_last4 || null}
                                        />
                                    </div>
                                );
                            }
                            return (
                            <div key={f.key} className="space-y-1.5">
                                <label className="text-sm font-medium text-slate-900 flex items-center gap-2 flex-wrap" htmlFor={`${prefix}-${f.key}`}>
                                    <span>
                                        {f.label}{f.required && <span className="text-red-500 ml-1">*</span>}
                                    </span>
                                    {/* Phase QCM-EIN regression fix —
                                        render the "Verified from FMCSA"
                                        badge next to the EIN label so
                                        it stays visible whether the
                                        FMCSA sections are collapsed or
                                        expanded. Comparison is
                                        digit-only via
                                        `isEinVerifiedFromFmcsa`, so
                                        "20-8148260" and "208148260"
                                        both match an FMCSA-reported
                                        208148260. */}
                                    {f.einMask && fmcsaQcm?.ein != null && isEinVerifiedFromFmcsa(data?.[f.key], fmcsaQcm.ein) && (
                                        <span
                                            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-800"
                                            data-testid={`${prefix}-${f.key}-fmcsa-verified-badge`}
                                            title="Matches the current FMCSA snapshot for this carrier"
                                        >
                                            <ShieldCheck size={10} /> Verified from FMCSA
                                        </span>
                                    )}
                                </label>
                                <input
                                    id={`${prefix}-${f.key}`}
                                    data-testid={`${prefix}-${f.key}`}
                                    type={f.type || "text"}
                                    inputMode={f.einMask ? "numeric" : f.inputMode}
                                    maxLength={f.einMask ? 10 : undefined}
                                    placeholder={f.placeholder || ""}
                                    value={data?.[f.key] || ""}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        let next;
                                        if (f.einMask) {
                                            // Phase QCM-EIN — mask to
                                            // XX-XXXXXXX as the user
                                            // types. Paste of raw 9-digit
                                            // or dashed string both
                                            // resolve to dashed display.
                                            next = maskEinInput(raw);
                                        } else if (f.maxDigits) {
                                            next = raw.replace(/\D/g, "").slice(0, f.maxDigits);
                                        } else {
                                            next = raw;
                                        }
                                        setField(f.key, next);
                                        // Clear inline error while user is
                                        // typing; reassess on blur.
                                        if (f.einMask && fieldErrors[f.key]) {
                                            setFieldErrors((m) => ({ ...m, [f.key]: "" }));
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (!f.einMask) return;
                                        const v = e.target.value || "";
                                        if (v === "") {
                                            // Empty is allowed (field is
                                            // optional unless required by
                                            // field_config); clear error.
                                            setFieldErrors((m) => ({ ...m, [f.key]: "" }));
                                            return;
                                        }
                                        if (!isValidEin(v)) {
                                            setFieldErrors((m) => ({
                                                ...m,
                                                [f.key]: "EIN must be 9 digits (XX-XXXXXXX).",
                                            }));
                                        } else {
                                            setFieldErrors((m) => ({ ...m, [f.key]: "" }));
                                        }
                                    }}
                                    disabled={disabled}
                                    aria-invalid={Boolean(fieldErrors[f.key]) || undefined}
                                    className={`flex h-10 w-full rounded-md border bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-900/30 disabled:bg-slate-50 ${fieldErrors[f.key] ? "border-red-300 focus:ring-red-500/30" : "border-slate-200"}`}
                                />
                                {f.helper && (
                                    <p className="text-xs text-slate-500" data-testid={`${prefix}-${f.key}-help`}>{f.helper}</p>
                                )}
                                {fieldErrors[f.key] && (
                                    <p
                                        className="text-xs text-red-700"
                                        role="alert"
                                        data-testid={`${prefix}-${f.key}-error`}
                                    >
                                        {fieldErrors[f.key]}
                                    </p>
                                )}
                            </div>
                            );
                        })}
                    </div>
                </CollapsibleSection>
            ))}
            <CollapsibleSection
                title="References"
                sectionKey={SECTION_KEYS.References}
                collapsed={collapsed.has(SECTION_KEYS.References)}
                forceOpen={forceOpenSlugs.has(SECTION_KEYS.References)}
                onToggle={toggleSection}
                testidPrefix={prefix}
            >
                <div className="space-y-4">
                    {COMPOSITES.map((c) => (
                        <fieldset key={c.key} className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
                            <legend className="px-1 text-sm font-medium text-slate-900">{c.label}</legend>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {c.subfields.map((sf) => (
                                    <div key={sf.key} className="space-y-1">
                                        <label className="text-xs text-slate-600" htmlFor={`${prefix}-${c.key}-${sf.key}`}>
                                            {sf.label}
                                        </label>
                                        {sf.key === "phone" ? (
                                            <PhoneInput
                                                data-testid={`${prefix}-${c.key}-${sf.key}`}
                                                value={(data?.[c.key] || {})[sf.key] || ""}
                                                onChange={(v) => setSub(c.key, sf.key, v)}
                                                disabled={disabled}
                                            />
                                        ) : (
                                            <input
                                                id={`${prefix}-${c.key}-${sf.key}`}
                                                data-testid={`${prefix}-${c.key}-${sf.key}`}
                                                type="text"
                                                value={(data?.[c.key] || {})[sf.key] || ""}
                                                onChange={(e) => setSub(c.key, sf.key, e.target.value)}
                                                disabled={disabled}
                                                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900/30 disabled:bg-slate-50"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </fieldset>
                    ))}
                </div>
            </CollapsibleSection>
        </div>
    );
}
